import { transform } from "ol/proj";
import { toBoolean } from "./../../util/toBoolean";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON.js";
import {
  Circle as CircleStyle,
  Fill,
  Stroke,
  Style,
  Icon as IconStyle,
  RegularShape,
} from "ol/style.js";
import Feature from "ol/Feature";
import { ILayerFactory } from "./ILayerFactory";
import { Map } from "ol";
import { SelectedFeatureInfo } from "../SelectedFeatureInfo";
import { FeatureGeoJson } from "../FeatureGeoJson";
import { AttributeType } from "../define/AttributeType";
import { GeoJsonAttributeConvert } from "@/attribute/convert/GeoJsonAttributeConvert";
import { MapContextMenuItem } from "../MapContextMenuItem";
import { Layer } from "ol/layer";
import {
  Geometry,
  GeometryCollection,
  Point,
  Circle as CircleGeom,
  MultiPoint,
} from "ol/geom";
import { featureGroup } from "../util/geoJsonData";
import { LayerUtil } from "../util/LayerUtil";

/**
 * コンテキストメニュー名
 */
const CONTEXT_MENU_NAME = "ベクタ属性";

/**
 * コンテキストメニュー名
 */
const ATTRIBUTE_TYPE = AttributeType.Normal;

/**
 * ベクタファイル読み込みレイヤファクトリ
 */
export class GeoJsonFactory implements ILayerFactory {
  /**
   * ベクタレイヤ
   */
  private _vectorLayer: VectorLayer<VectorSource>;

  /**
   * ベクタソース
   */
  private _vectorSource: VectorSource;

  /**
   * 地物情報があるか
   */
  isFeatureInfo(): boolean {
    return true;
  }

  /**
   * 範囲選択地物情報があるか
   */
  isRangeFeatureInfo(): boolean {
    return false;
  }

  /**
   * 右クリック情報があるか
   */
  isRightClickInfo(): boolean {
    return false;
  }

  /**
   * 表示中のレイヤ数を取得
   */
  getVisibleLayerCount(): number {
    return this._vectorLayer?.getVisible() ? 1 : 0;
  }

  constructor() {
    this._vectorSource = new VectorSource();
    this._vectorLayer = new VectorLayer({ source: this._vectorSource });
  }

  /**
   * レイヤを取得
   */
  getViewLayer(): VectorLayer<VectorSource>[] {
    return [this._vectorLayer];
  }

  /**
   * GeoJSONを読み込んでレイヤに追加
   * @param loadedGeoJson 読み込みGeoJSON
   */
  load(loadedGeoJson: any): Array<number> {
    const styleFunction = function (feature: Feature) {
      const geometry = feature.getGeometry();
      // GeometryCollectionの場合、geometries内のgeometry毎にスタイルを付与
      if (geometry instanceof GeometryCollection) {
        const geometries = geometry.getGeometries();
        const polygonStyle = new Style({
          geometry: new GeometryCollection(
            geometries.filter((geom) => {
              return (
                geom.getType() === "Polygon" ||
                geom.getType() === "MultiPolygon"
              );
            })
          ),
          stroke: new Stroke({
            color: [0, 0, 0],
            width: 1,
          }),
          fill: new Fill({ color: [255, 153, 204] }),
        });
        const lineStyle = new Style({
          geometry: new GeometryCollection(
            geometries.filter((geom) => {
              return (
                geom.getType() === "LineString" ||
                geom.getType() === "MultiLineString"
              );
            })
          ),
          stroke: new Stroke({
            color: [255, 153, 204],
            width: 1,
          }),
        });
        const pointStyle = new Style({
          geometry: new GeometryCollection(
            geometries.filter((geom) => {
              return (
                geom.getType() === "Point" || geom.getType() === "MultiPoint"
              );
            })
          ),
          image: new CircleStyle({
            radius: 11,
            stroke: new Stroke({
              color: [0, 0, 0],
              width: 1,
            }),
            fill: new Fill({ color: [255, 153, 204] }),
          }),
        });
        return [polygonStyle, lineStyle, pointStyle];
      } else {
        const featureType = feature.getGeometry().getType();
        let featureStyles: any;
        const featureProperties = feature.getProperties();

        // カラーコード 16進数→RGB
        const convertColorCode = (hexCode: string) => {
          hexCode = hexCode.replace(/^#/, "");
          if (hexCode.length === 3) {
            hexCode = hexCode
              .split("")
              .map((ele) => ele + ele)
              .join("");
          }
          const red = parseInt(hexCode.substring(0, 2), 16);
          const green = parseInt(hexCode.substring(2, 4), 16);
          const blue = parseInt(hexCode.substring(4, 6), 16);

          return [red, green, blue];
        };
        let radius: number;
        let strokeColor: number[];
        let width: number;
        let lineDash: number[];
        let fillColor: number[];
        let lineCap: CanvasLineCap;
        let lineJoin: CanvasLineJoin;
        let anchor: number[];
        let size: number[];
        switch (featureType) {
          case "Point":
          case "MultiPoint":
            if (
              featureProperties._markerType === "CircleMarker" ||
              featureProperties._markerType === "Circle" ||
              !featureProperties._markerType
            ) {
              strokeColor = [0, 0, 0];
              width = 2;
              fillColor = [255, 153, 204];
              if (featureProperties._color) {
                strokeColor = convertColorCode(featureProperties._color);
              }
              if (featureProperties._weight) {
                width = featureProperties._weight;
              }
              if (featureProperties._opacity) {
                strokeColor[3] = featureProperties._opacity;
              }
              if (featureProperties._dashArray) {
                lineDash = featureProperties._dashArray.split(" ").map(Number);
              }
              if (featureProperties._fillColor) {
                fillColor = convertColorCode(featureProperties._fillColor);
              }
              if (featureProperties._fillOpacity) {
                fillColor[3] = featureProperties._fillOpacity;
              }
              if (
                featureProperties._markerType === "CircleMarker" ||
                !featureProperties._markerType
              ) {
                radius = 11;
                if (featureProperties._radius) {
                  radius = featureProperties._radius;
                }
                featureStyles = new Style({
                  image: new CircleStyle({
                    radius,
                    stroke:
                      featureProperties._stroke == undefined ||
                      toBoolean(featureProperties._stroke)
                        ? new Stroke({
                            color: strokeColor,
                            width,
                            lineDash,
                          })
                        : null,
                    fill:
                      featureProperties._fill == undefined ||
                      toBoolean(featureProperties._fill)
                        ? new Fill({
                            color: fillColor,
                          })
                        : null,
                  }),
                });
              } else {
                featureStyles = new Style({
                  stroke:
                    featureProperties._stroke == undefined ||
                    toBoolean(featureProperties._stroke)
                      ? new Stroke({
                          color: strokeColor,
                          width,
                          lineDash,
                        })
                      : null,
                  fill:
                    featureProperties._fill == undefined ||
                    toBoolean(featureProperties._fill)
                      ? new Fill({
                          color: fillColor,
                        })
                      : null,
                });
              }
            } else if (featureProperties._markerType === "Icon") {
              if (featureProperties._iconUrl) {
                anchor = [0.5, 0.5];
                if (featureProperties._fill && featureProperties._fillColor) {
                  fillColor = convertColorCode(featureProperties._fillColor);
                  if (featureProperties._fillOpacity) {
                    fillColor[3] = featureProperties._fillOpacity;
                  }
                }
                const randStr = Math.random()
                  .toString(36)
                  .substring(2)
                  .slice(-7);
                featureStyles = new Style({
                  image: new IconStyle({
                    anchorXUnits: "fraction",
                    anchorYUnits: "fraction",
                    src: featureProperties._iconUrl + "?token=" + randStr,
                    opacity: 1,
                    color: fillColor,
                    crossOrigin: "anonymous",
                  }),
                });

                let loadedFlg = false;
                const iconImage: any = featureStyles.getImage().getImage();
                iconImage.onload = () => {
                  if (loadedFlg) return;
                  loadedFlg = true;
                  if (featureProperties._iconSize) {
                    size = featureProperties._iconSize;
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    const scaleX = size[0] / iconImage.width;
                    const scaleY = size[1] / iconImage.height;
                    canvas.width = size[0];
                    canvas.height = size[1];
                    ctx.scale(scaleX, scaleY);
                    ctx.drawImage(iconImage, 0, 0);

                    const src = canvas.toDataURL();
                    featureStyles = new Style({
                      image: new IconStyle({
                        anchorXUnits: "fraction",
                        anchorYUnits: "fraction",
                        src,
                        opacity: 1,
                        color: fillColor,
                        crossOrigin: "anonymous",
                      }),
                    });
                    feature.setStyle(featureStyles);
                    if (featureProperties._iconAnchor) {
                      anchor = [0, 1].map((ele) => {
                        return featureProperties._iconAnchor[ele] / size[ele];
                      });
                    }
                  } else if (featureProperties._iconAnchor) {
                    anchor = [
                      featureProperties._iconAnchor[0] / iconImage.width,
                      featureProperties._iconAnchor[1] / iconImage.height,
                    ];
                  }
                  featureStyles.getImage().setAnchor(anchor);
                };
              } else {
                featureStyles = new Style({
                  image: null,
                });
              }
            }
            break;
          case "LineString":
          case "MultiLineString":
            strokeColor = [255, 153, 204];
            width = 2;
            if (featureProperties._color) {
              strokeColor = convertColorCode(featureProperties._color);
            }
            if (featureProperties._weight) {
              width = featureProperties._weight;
            }
            if (featureProperties._opacity) {
              strokeColor[3] = featureProperties._opacity;
            }
            if (featureProperties._dashArray) {
              lineDash = featureProperties._dashArray.split(" ").map(Number);
            }
            if (featureProperties._lineCap) {
              lineCap = featureProperties._lineCap;
            }
            if (featureProperties._lineJoin) {
              lineJoin = featureProperties._lineJoin;
            }
            featureStyles = new Style({
              stroke:
                featureProperties._stroke == undefined ||
                toBoolean(featureProperties._stroke)
                  ? new Stroke({
                      color: strokeColor,
                      width,
                      lineDash,
                      lineCap,
                      lineJoin,
                    })
                  : null,
            });
            break;
          case "Polygon":
          case "MultiPolygon":
            strokeColor = [0, 0, 0];
            fillColor = [255, 153, 204];
            width = 1;
            if (featureProperties._color) {
              strokeColor = convertColorCode(featureProperties._color);
            }
            if (featureProperties._weight) {
              width = featureProperties._weight;
            }
            if (featureProperties._opacity) {
              strokeColor[3] = featureProperties._opacity;
            }
            if (featureProperties._dashArray) {
              lineDash = featureProperties._dashArray.split(" ").map(Number);
            }
            if (featureProperties._lineJoin) {
              lineJoin = featureProperties._lineJoin;
            }
            if (featureProperties._fillColor) {
              fillColor = convertColorCode(featureProperties._fillColor);
            }
            if (featureProperties._fillOpacity) {
              fillColor[3] = featureProperties._fillOpacity;
            }
            featureStyles = new Style({
              stroke:
                featureProperties._stroke == undefined ||
                toBoolean(featureProperties._stroke)
                  ? new Stroke({
                      color: strokeColor,
                      width,
                      lineDash,
                      lineJoin,
                    })
                  : null,
              fill:
                featureProperties._fill == undefined ||
                toBoolean(featureProperties._fill)
                  ? new Fill({
                      color: fillColor,
                    })
                  : null,
            });
            break;
        }
        return featureStyles;
      }
    };

    // GeoJSONに座標系が設定されていた場合、Feature生成時に指定
    const dataProjection = new GeoJSON().readProjection(loadedGeoJson);

    const featuresOptions = {
      dataProjection,
      featureProjection: "EPSG:3857",
    };

    const features = new GeoJSON().readFeatures(loadedGeoJson, featuresOptions);
    features.forEach((feature) => {
      feature.setStyle(styleFunction(feature));
      if (feature.getProperties()._markerType === "Circle") {
        const geometry = feature.getGeometry();
        if (geometry instanceof Point) {
          const coordinate = geometry.getCoordinates();
          feature.setGeometry(
            new CircleGeom(
              coordinate,
              feature.getProperties()._radius
                ? Number(feature.getProperties()._radius)
                : 11
            )
          );
        } else if (geometry instanceof MultiPoint) {
          const coordinates = geometry.getCoordinates();
          const groupIndex = featureGroup.length;
          featureGroup.push(coordinates);
          coordinates.forEach((coordinate) => {
            const clonedFeature = feature.clone();
            clonedFeature.setGeometry(
              new CircleGeom(
                coordinate,
                feature.getProperties()._radius
                  ? Number(feature.getProperties()._radius)
                  : 11
              )
            );
            (clonedFeature as any).groupIndex = groupIndex;
            this._vectorSource.addFeatures([clonedFeature]);
          });
        }
      }
    });
    this._vectorSource.addFeatures(features);

    // 読込ファイル内で最初に指定されている座標にジャンプ
    if (features.length) {
      const geometry: any = features[0].getGeometry();
      let coordinates;
      if (typeof geometry.getCenter === "function") {
        coordinates = geometry.getCenter();
      } else if (typeof geometry.getCoordinates === "function") {
        coordinates = geometry.getCoordinates();
      }

      if (coordinates) {
        const unnestCoordinates: any = (coordinates: Array<any>) => {
          if (Array.isArray(coordinates) && Array.isArray(coordinates[0])) {
            coordinates = coordinates[0];
            return unnestCoordinates(coordinates);
          }
          return coordinates;
        };

        coordinates = unnestCoordinates(coordinates);
        coordinates = transform(coordinates, "EPSG:3857", "EPSG:4326");
        return coordinates;
      }
    }
  }

  /**
   * 読み込み済みベクタ情報をクリア
   */
  clear(): void {
    this._vectorSource.clear();
  }

  /**
   * 地物情報を取得
   */
  getFeatureInfo(
    map: Map,
    latitude: number,
    longitude: number,
    x: number,
    y: number
  ): Promise<SelectedFeatureInfo[]> {
    const promise: Promise<SelectedFeatureInfo[]> = new Promise((resolve) => {
      const pixel = [x, y];
      const features = map.getFeaturesAtPixel(pixel, {
        layerFilter: (layer: Layer) => {
          if (this._vectorLayer == layer) {
            return true;
          }
        },
      });
      const selectedFeatureInfoList = features.map((feature) => {
        const selectFeature = new Feature({
          geometry: feature.getGeometry() as Geometry,
        });
        // 点座標スタイル
        const circleImage = new CircleStyle({
          radius: feature.getProperties()._radius ?? 11,
          stroke: new Stroke({
            color: "hotpink",
            width: feature.getProperties()._weight ?? 2,
          }),
        });
        const stroke = new Stroke({
          color: "hotpink",
          width: feature.getProperties()._weight ?? 2,
        });
        const styles = {
          Point: new Style({
            image: circleImage,
          }),
          LineString: new Style({
            stroke,
          }),
          Polygon: new Style({
            stroke,
          }),
          MultiPoint: new Style({
            image: circleImage,
          }),
          MultiLineString: new Style({
            stroke,
          }),
          MultiPolygon: new Style({
            stroke,
          }),
          GeometryCollection: new Style({
            stroke,
            image: circleImage,
          }),
          LinearRing: new Style({}),
          Circle: new Style({ stroke }),
        };
        const styleFunction = function (feature: Feature) {
          return styles[feature.getGeometry().getType()];
        };
        // スタイルを設定
        if (["Point", "MultiPoint"].includes(feature.getGeometry().getType())) {
          if (feature.getProperties()._markerType === "Icon") {
            const featureStyles: any = feature.getStyleFunction()(feature, 5);
            const imageSize = featureStyles[0].getImage().getImageSize();
            const width = featureStyles[0].getImage().getImage().width;
            const height = featureStyles[0].getImage().getImage().height;
            const squareImage = new RegularShape({
              stroke: new Stroke({
                width: 4,
                color: "hotpink",
              }),
              points: 4,
              radius:
                Math.max(
                  width ? width : imageSize[0],
                  height ? height : imageSize[1]
                ) / Math.sqrt(2),
              angle: Math.PI / 4,
            });
            const iconPointStyle = new Style({
              image: squareImage,
            });
            selectFeature.setStyle(iconPointStyle);
          } else {
            selectFeature.setStyle(styles.Point);
          }
        } else {
          selectFeature.setStyle(styleFunction(selectFeature));
        }

        const geoJson = new GeoJSON().writeFeature(feature as Feature);
        const id = `GeoJSON-${longitude}.${latitude}`;
        const featureGeoJSON = new FeatureGeoJson(geoJson, id);
        const attributeConvert = new GeoJsonAttributeConvert(geoJson);
        return new SelectedFeatureInfo(
          selectFeature,
          CONTEXT_MENU_NAME,
          featureGeoJSON,
          ATTRIBUTE_TYPE,
          attributeConvert,
          false,
          null,
          null,
          null,
          null,
          (feature as any).groupIndex
        );
      });
      if (selectedFeatureInfoList.length) {
        resolve(selectedFeatureInfoList);
      }
      resolve([]);
    });
    return promise;
  }

  /**
   * 指定した範囲の地物情報を取得（null返却）
   */
  getRangeFeatureInfo(): Promise<SelectedFeatureInfo[]> {
    return null;
  }

  /**
   * 右クリック情報を取得（null返却）
   */
  getRightClickInfo(): Promise<MapContextMenuItem[]> {
    return null;
  }
  /**
   * 表示レイヤが存在するか判定
   */
  isVisibleLayer(_map: Map) {
    return LayerUtil.isVisibleLayer(_map, this._vectorLayer);
  }
}
